import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import adminQueueService from '../../../../../services/adminQueueService';
import ManagerModal from './ManagerModal';

function ManagerList() {
	const { t } = useTranslation();
	const [managers, setManagers] = useState([]);
	const [serviceTypes, setServiceTypes] = useState([]);
	const [windows, setWindows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [totalElements, setTotalElements] = useState(0);
	const rowsPerPage = 20;

	const [modalOpen, setModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState(null);

	const [deleteId, setDeleteId] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const [togglingId, setTogglingId] = useState(null);

	const fetchManagers = async (p = page) => {
		setLoading(true);
		try {
			const res = await adminQueueService.getAllManagers(p, rowsPerPage);
			setManagers(res.data.content || []);
			setTotalElements(res.data.page?.totalElements ?? res.data.totalElements ?? 0);
		} catch {
			toast.error(t('admin.managers.loadError'));
		} finally {
			setLoading(false);
		}
	};

	const fetchServiceTypes = async () => {
		try {
			const res = await adminQueueService.getAllServiceTypes(0, 200);
			setServiceTypes(res.data.content || []);
		} catch {
			// serviceTypes used only in modal, non-critical
		}
	};

	const fetchWindows = async () => {
		try {
			const res = await adminQueueService.getWindows();
			setWindows(res.data || []);
		} catch {
			// windows used only in modal, non-critical
		}
	};

	useEffect(() => {
		fetchManagers(page);
		fetchServiceTypes();
		fetchWindows();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	const handleAdd = () => {
		fetchWindows();
		setEditingItem(null);
		setModalOpen(true);
	};

	const handleEdit = (item) => {
		fetchWindows();
		setEditingItem(item);
		setModalOpen(true);
	};

	const handleModalSuccess = () => {
		setModalOpen(false);
		fetchManagers(page);
		fetchWindows();
	};

	const handleDeleteConfirm = async () => {
		setDeleting(true);
		try {
			await adminQueueService.deleteManager(deleteId);
			toast.success(t('admin.managers.deletedSuccess'));
			setDeleteId(null);
			const newPage =
				managers.length === 1 && page > 0 ? page - 1 : page;
			setPage(newPage);
			fetchManagers(newPage);
			fetchWindows();
		} catch (e) {
			toast.error(
				e?.response?.data?.message || t('admin.managers.deletedError')
			);
		} finally {
			setDeleting(false);
		}
	};

	const handleToggleActive = async (manager) => {
		setTogglingId(manager.id);
		try {
			const fullName = `${manager.surname} ${manager.name}`;
			if (manager.isActive) {
				await adminQueueService.deactivateManager(manager.id);
				toast.success(t('admin.managers.deactivatedSuccess', { name: fullName }));
			} else {
				await adminQueueService.activateManager(manager.id);
				toast.success(t('admin.managers.activatedSuccess', { name: fullName }));
			}
			fetchManagers(page);
		} catch (e) {
			toast.error(
				e?.response?.data?.message || t('admin.managers.toggleError')
			);
		} finally {
			setTogglingId(null);
		}
	};

	return (
		<Box>
			<Stack
				direction='row'
				justifyContent='space-between'
				alignItems='center'
				sx={{ mb: 2.5 }}
			>
				<Typography variant='h6' sx={{ fontWeight: 700 }}>
					{t('admin.managers.title')}{totalElements > 0 && ` (${totalElements})`}
				</Typography>
				<Button
					variant='contained'
					startIcon={<AddRoundedIcon />}
					onClick={handleAdd}
					sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
				>
					{t('admin.managers.addBtn')}
				</Button>
			</Stack>

			<Paper
				elevation={0}
				sx={{
					borderRadius: 3,
					border: '1px solid rgba(0,0,0,0.08)',
					overflow: 'hidden'
				}}
			>
				<TableContainer>
					<Table size='medium'>
						<TableHead>
							<TableRow sx={{ backgroundColor: 'rgba(25,118,210,0.06)' }}>
								<TableCell sx={{ fontWeight: 700, width: 50 }}>№</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.managers.colName')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.managers.colEmail')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.managers.colPhone')}</TableCell>
								<TableCell sx={{ fontWeight: 700 }}>{t('admin.managers.colServiceTypes')}</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 90 }}>{t('admin.managers.colWindow')}</TableCell>
								<TableCell sx={{ fontWeight: 700, width: 110 }}>{t('admin.managers.colStatus')}</TableCell>
								<TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>{t('admin.managers.colActions')}</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={8} align='center' sx={{ py: 5 }}>
										<CircularProgress size={32} />
									</TableCell>
								</TableRow>
							) : managers.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={8}
										align='center'
										sx={{ py: 5, color: 'text.secondary' }}
									>
										{t('admin.managers.empty')}
									</TableCell>
								</TableRow>
							) : (
								managers.map((mgr, idx) => (
									<TableRow
										key={mgr.id}
										hover
										sx={{ '&:last-child td': { borderBottom: 0 } }}
									>
										<TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
											{page * rowsPerPage + idx + 1}
										</TableCell>
										<TableCell>
											<Box>
												<Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.2 }}>
													{mgr.surname} {mgr.name}
												</Typography>
												{mgr.patronymic && (
													<Typography variant='caption' color='text.secondary'>
														{mgr.patronymic}
													</Typography>
												)}
											</Box>
										</TableCell>
										<TableCell sx={{ fontSize: 13 }}>{mgr.email}</TableCell>
										<TableCell sx={{ fontSize: 13 }}>{mgr.phoneNumber}</TableCell>
										<TableCell>
											<Stack direction='row' flexWrap='wrap' gap={0.5}>
												{(mgr.serviceTypes || []).map((st) => (
													<Chip
														key={st.id}
														label={st.nameRu}
														size='small'
														sx={{
															backgroundColor: st.color || '#EDE9FE',
															fontWeight: 600,
															fontSize: 11
														}}
													/>
												))}
												{(!mgr.serviceTypes || mgr.serviceTypes.length === 0) && (
													<Typography variant='caption' color='text.secondary'>—</Typography>
												)}
											</Stack>
										</TableCell>
										<TableCell>
											<Chip
												label={mgr.windowNumber}
												size='small'
												variant='outlined'
												color='primary'
												sx={{ fontWeight: 700 }}
											/>
										</TableCell>
										<TableCell>
											<Chip
												label={mgr.isActive ? t('admin.managers.statusActive') : t('admin.managers.statusInactive')}
												size='small'
												color={mgr.isActive ? 'success' : 'default'}
											/>
										</TableCell>
										<TableCell align='right'>
											<Stack direction='row' justifyContent='flex-end' spacing={0.25}>
												<Tooltip title={mgr.isActive ? t('admin.managers.tooltipDeactivate') : t('admin.managers.tooltipActivate')}>
													<span>
														<IconButton
															size='small'
															color={mgr.isActive ? 'warning' : 'success'}
															onClick={() => handleToggleActive(mgr)}
															disabled={togglingId === mgr.id}
														>
															{togglingId === mgr.id ? (
																<CircularProgress size={16} />
															) : (
																<PowerSettingsNewRoundedIcon fontSize='small' />
															)}
														</IconButton>
													</span>
												</Tooltip>
												<Tooltip title={t('admin.managers.tooltipEdit')}>
													<IconButton size='small' color='primary' onClick={() => handleEdit(mgr)}>
														<EditRoundedIcon fontSize='small' />
													</IconButton>
												</Tooltip>
												<Tooltip title={t('admin.managers.tooltipDelete')}>
													<IconButton size='small' color='error' onClick={() => setDeleteId(mgr.id)}>
														<DeleteRoundedIcon fontSize='small' />
													</IconButton>
												</Tooltip>
											</Stack>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component='div'
					count={totalElements}
					page={page}
					onPageChange={(_, p) => setPage(p)}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[20]}
					labelDisplayedRows={({ from, to, count }) =>
						t('common.from_to_count', { from, to, count })
					}
				/>
			</Paper>

			<ManagerModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				onSuccess={handleModalSuccess}
				editingItem={editingItem}
				serviceTypes={serviceTypes}
				windows={windows}
			/>

			<Dialog
				open={!!deleteId}
				onClose={() => !deleting && setDeleteId(null)}
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle sx={{ fontWeight: 700 }}>{t('admin.managers.deleteTitle')}</DialogTitle>
				<DialogContent>
					<Typography variant='body2' color='text.secondary'>
						{t('admin.managers.deleteBody')}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
					<Button
						onClick={() => setDeleteId(null)}
						disabled={deleting}
						sx={{ textTransform: 'none', borderRadius: 2 }}
					>
						{t('common.cancel')}
					</Button>
					<Button
						variant='contained'
						color='error'
						onClick={handleDeleteConfirm}
						disabled={deleting}
						sx={{ textTransform: 'none', borderRadius: 2, minWidth: 100 }}
					>
						{deleting ? (
							<CircularProgress size={18} sx={{ color: 'inherit' }} />
						) : (
							t('common.delete')
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default ManagerList;
